import ComplaintsService from './complaints/ComplaintsService';
import AuthService from './customer/AuthService';
import CustomerService from './customer/CustomerService';
import DesignService from './designs/DesignService';
import FileDeliveryService from './file-delivery/FileDeliveryService';
import { HelpCenterService } from './help-center/HelpCenterService';
import { HttpClientFactory } from './HttpClientFactory';
import InvoiceService from './invoice/InvoiceService';
import ItpService from './itp/ItpService';
import PaymentV2Service from './payment/PaymentV2Service';
import ProductService from './product/ProductService';
import ValidationService from './product/ValidationService';
import QuotesService from './quotes/QuotesService';
import ArtworkService from './artwork/ArtworkService';
import PrintQService from './printQ/PrintQService';
import Services from './service/Services';
import { PortalMessage } from './content/PortalMessage';
import { ContentfulService, buildContenfulBaseURL } from './contentful/ContentfulService';
import CustomerProposalService from './customer/CustomerProposalService';
import SurfaceDependencyService from './product/SurfaceDependencyService';
import ProductAiService from './product-ai/ProductAiService';
import DynamicProductRoutesService from './product/DynamicProductRoutesService';
import ApiCredentialsService from './apiCredentials/ApiCredentialsService';
import TransformationService from './product/TransformationService';
import CustomerApiService from './customer/CustomerApiService';
import portalConfiguration from '../../static/configuration/configuration.json';
import BusinessRulesService from '../components/business-rules/services/BusinessRulesService';

class ServiceFactory {
  static createAuthService() {
    return new AuthService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_AUTH_ENDPOINT as string,
    ));
  }

  static createCustomerService() {
    return new CustomerService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_CUSTOMER_URL as string,
    ));
  }

  static createCustomerApiService() {
    return new CustomerApiService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_CUSTOMER_API_URL as string,
    ));
  }

  static createCustomerProposalService() {
    return new CustomerProposalService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_FILE_REVIEW_SERVICE_URL as string,
    ));
  }

  static createPaymentV2Service(): PaymentV2Service {
    return new PaymentV2Service(HttpClientFactory.createHttpClient(
      process.env.GATSBY_LAUNCHPAD_PAYMENT_V2_URL as string,
    ));
  }

  static createProductService(): ProductService {
    return new ProductService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_PRODUCT_ENDPOINT as string,
    ));
  }

  static createDesignService(): DesignService {
    return new DesignService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_DESIGNS_ENDPOINT as string,
    ));
  }

  static createQuotesService(): QuotesService {
    return new QuotesService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_QUOTES_ENDPOINT as string,
    ));
  }

  static createApiCredentialsService(): ApiCredentialsService {
    return new ApiCredentialsService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_API_CREDENTIALS_ENDPOINT as string,
    ));
  }

  static createInvoiceService(): InvoiceService {
    return new InvoiceService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_INVOICE_V2_URL as string,
    ));
  }

  static createComplaintsService(): ComplaintsService {
    return new ComplaintsService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_LAUNCH_COMPLAINT_URL as string,
    ));
  }

  static createFileDeliveryService(): FileDeliveryService {
    return new FileDeliveryService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_FILE_DELIVERY_URL as string,
    ));
  }

  static createHelpCenterService(url?: string): HelpCenterService {
    return new HelpCenterService(HttpClientFactory.createHttpClient(
      url || portalConfiguration.baseUrls.HELP_CENTER_API as string,
    ));
  }

  static createValidationService(): ValidationService {
    return new ValidationService(HttpClientFactory.createCOAMClient(
      process.env.GATSBY_PRODUCT_VALIDATION_URL as string,
    ));
  }

  static createSurfaceDependencyService(): SurfaceDependencyService {
    return new SurfaceDependencyService(HttpClientFactory.createCOAMClient(
      process.env.GATSBY_DESIGN_TEMPLATE_GENERATOR_BASE_URL as string,
    ));
  }

  static createTransformationService(): TransformationService {
    return new TransformationService(HttpClientFactory.createCOAMClient(
      process.env.GATSBY_TRANSFORMATIONS_URL as string,
    ));
  }

  static createItpService(): ItpService {
    return new ItpService();
  }

  static createArtworkService(): ArtworkService {
    return new ArtworkService(HttpClientFactory.createHttpClient(
      portalConfiguration.baseUrls.ARTWORK_API,
    ));
  }

  static productAIService(): ProductAiService {
    return new ProductAiService(HttpClientFactory.createHttpClient(
      portalConfiguration.baseUrls.PRODUCT_AI_API,
    ));
  }

  static dynamicProductRoutesService(): DynamicProductRoutesService {
    return new DynamicProductRoutesService(HttpClientFactory.createHttpClient(
      portalConfiguration.baseUrls.DYNAMIC_PRODUCT_ROUTES,
    ));
  }

  static createServices(): Services {
    return new Services(HttpClientFactory.createHttpClient(
      process.env.GATSBY_SERVICES_ENDPOINT as string,
    ));
  }

  static createPortalMessageService(): PortalMessage {
    return new PortalMessage(HttpClientFactory.createHttpClient(
      process.env.GATSBY_SYSTEM_STATUS_URL as string,
      false,
    ));
  }

  static createContentfulService() {
    return new ContentfulService(HttpClientFactory.createContenfulClient(buildContenfulBaseURL(
      process.env.GATSBY_CONTENTFUL_GRAPHQL_API_BASE_URL as string,
      process.env.GATSBY_CONTENTFUL_SPACE_ID as string,
      process.env.GATSBY_CONTENTFUL_ENVIRONMENT as string,
    )));
  }

  static createPrintQService(): PrintQService {
    return new PrintQService(HttpClientFactory.createHttpClient(
      process.env.GATSBY_PRINTQ_API_BASE_URL as string,
    ));
  }

  static createBusinessRulesService(): BusinessRulesService {
    return new BusinessRulesService(HttpClientFactory.createHttpClient(
      portalConfiguration.baseUrls.BUSINESS_RULES_API,
    ));
  }
}

export { ServiceFactory };
